import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Play, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { RawanPlayer } from "@/components/rawan/RawanPlayer";
import { AdSlot } from "@/components/rawan/AdSlot";
import { getItemBySlug, useAllItems } from "@/lib/library-store";
import { getEpisodesForSeries } from "@/lib/episodes-store";
import { getEpisode as getContentEpisodeRaw, getSeasonsForSeries as getSeasonsRaw } from "@/lib/content/loader";
import { applyOverrideToEpisode, getMergedSeasonsForSeries, useEpisodeOverrides } from "@/lib/episode-overrides";
const getContentEpisode = (slug: string, season: number, number: number) => {
  const raw = getContentEpisodeRaw(slug, season, number);
  return raw ? applyOverrideToEpisode(slug, season, raw) : undefined;
};
const getSeasonsForSeries = getMergedSeasonsForSeries;
void getSeasonsRaw;
import { useTheme } from "@/lib/theme-context";
import { detectHeadLang, pickT, pickTitle } from "@/lib/i18n";
import { localizeEpisodeTitle } from "@/lib/episode-title";
import { useDeviceClass } from "@/hooks/use-device";
import { consumeInternalNav } from "@/lib/internal-nav";


export const Route = createFileRoute("/watch/$slug/$ep")({
  loader: ({ params }) => {
    const series = getItemBySlug(params.slug);
    return { series: series ?? null, slug: params.slug, ep: params.ep };
  },
  head: ({ loaderData, params, match }) => {
    const s = loaderData?.series;
    const m = (params.ep || "").toLowerCase().match(/^s(\d+)e(\d+)$/);
    const season = m ? parseInt(m[1], 10) : undefined;
    const number = m ? parseInt(m[2], 10) : undefined;
    const ep = s && season && number ? getContentEpisode(params.slug, season, number) : undefined;
    const canonical = `/watch/${params.slug}/${params.ep}`;
    const lang = detectHeadLang(match.search as Record<string, unknown>);
    const seriesTitle = s ? pickTitle(s, lang) : "";
    const episodeTitle = localizeEpisodeTitle(ep?.title, lang) || pickT(lang, "حلقة", "Episode", "Épisode");
    const title = ep && s ? `${seriesTitle} — S${season}·E${number}: ${episodeTitle}` : pickT(lang, "حلقة", "Episode", "Épisode");
    const desc = s ? pickT(lang, `شاهد ${title} على Rawan UI بجودة عالية.`, `Watch ${title} on Rawan UI in high quality.`, `Regardez ${title} sur Rawan UI en haute qualité.`) : "";
    const image = ep?.thumbnail || s?.backdrop;
    return {
      meta: [
        { title: `${title} — Rawan UI` },
        { name: "description", content: desc },
        { name: "robots", content: "noindex, follow" },
        { property: "og:type", content: "video.episode" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        ...(image ? [{ property: "og:image", content: image }] : []),
        { property: "og:url", content: canonical },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image ? [{ name: "twitter:image", content: image }] : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: s && ep && season && number ? [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TVEpisode",
          name: episodeTitle,
          episodeNumber: number,
          partOfSeason: { "@type": "TVSeason", seasonNumber: season },
          partOfSeries: { "@type": "TVSeries", name: seriesTitle, image: s.poster },
          image: ep.thumbnail || s.backdrop,
          inLanguage: lang,
        }),
      }] : [],
    };
  },
  component: WatchPage,
});

function parseEp(code: string): { season: number; number: number } | null {
  const m = code.toLowerCase().match(/^s(\d+)e(\d+)$/);
  if (!m) return null;
  return { season: parseInt(m[1], 10), number: parseInt(m[2], 10) };
}

function WatchPage() {
  const { series: initial } = Route.useLoaderData();
  const { slug, ep } = Route.useParams();
  const navigate = useNavigate();
  const allItems = useAllItems();
  const series = initial ?? allItems.find((m) => m.slug === slug);
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => pickT(language, ar, en, fr);
  // ملاحظة: تم دمج مبدّل الجودة أسفل المشغل مع قائمة watch_urls الفعلية
  // (بدلاً من قائمة ثابتة 4K/HD/SD/Low لا تُغيّر المصدر). الآن كل زر جودة
  // يُبدّل مصدر التشغيل ويُرسل watch_token/URL الصحيحين لتلك الجودة.

  // الحاسوب: المشغل مُخصّص للهاتف والتابلت فقط — أعِد المستخدم إلى صفحة المسلسل.
  // الكشف يجمع بين الـ User-Agent ومؤشّر الإدخال (mouse/touch) ولا يعتمد على عرض النافذة فقط،
  // حتى لا يظهر المشغّل لمستخدم حاسوب صغّر نافذته.
  const device = useDeviceClass();
  useEffect(() => {
    if (!device.ready) return;
    if (device.isDesktop) { navigate({ to: "/series/$slug", params: { slug } }); return; }
    // حارس المشاركة: إذا فُتحت الصفحة من رابط خارجي (مشاركة)، نُحوّل إلى صفحة المسلسل.
    // بمجرد اجتياز الحارس لأيّ حلقة من المسلسل، نمنح إذناً على مستوى المسلسل في
    // نفس التبويب فقط (sessionStorage)، حتى يتمكن المستخدم من التبديل بين
    // الحلقات داخل المشغّل دون أن يُعيده الحارس إلى صفحة المسلسل.
    // مشاركة الرابط في تبويب آخر تبقى محجوبة كما كانت.
    const seriesKey = `rawan-nav-allow-series:${slug}`;
    let hasSeriesAllow = false;
    try { hasSeriesAllow = !!sessionStorage.getItem(seriesKey); } catch { /* */ }
    if (hasSeriesAllow) return;
    if (!consumeInternalNav(`/watch/${slug}/${ep}`)) {
      navigate({ to: "/series/$slug", params: { slug } });
      return;
    }
    try { sessionStorage.setItem(seriesKey, "1"); } catch { /* */ }
  }, [device.ready, device.isDesktop, navigate, slug, ep]);

  /* PWA: allow device rotation on the watch page so the player can rotate
     with the phone. The manifest ships with orientation:"any" but some
     installed contexts default to a locked portrait; we explicitly unlock
     while this route is mounted, and restore portrait on unmount so the
     rest of the app keeps its normal (portrait) behaviour. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const so = (window.screen as unknown as { orientation?: { unlock?: () => void; lock?: (o: string) => Promise<void> } }).orientation;
    if (!so) return;
    try { so.unlock?.(); } catch { /* ignore */ }
    return () => {
      try { so.lock?.("portrait").catch(() => { /* ignore */ }); } catch { /* ignore */ }
    };
  }, []);

  const parsed = parseEp(ep);

  // Record this episode as recently-watched (syncs across devices when
  // the viewer is signed in).
  useEffect(() => {
    if (!parsed) return;
    void import("@/lib/watch-history").then(({ recordWatch }) => {
      recordWatch({
        slug,
        kind: "series",
        season: parsed.season,
        episode: parsed.number,
      });
    }).catch(() => { /* ignore */ });
  }, [slug, parsed?.season, parsed?.number]);



  const overridesMap = useEpisodeOverrides();
  const overridesSig = useMemo(() => JSON.stringify(overridesMap), [overridesMap]);
  const contentEpisode = useMemo(() => {
    if (!parsed) return undefined;
    return getContentEpisode(slug, parsed.season, parsed.number);
  }, [slug, parsed?.season, parsed?.number, overridesSig]);
  const episode = useMemo(() => {
    if (!parsed) return undefined;
    return getEpisodesForSeries(slug).find((e) => e.season === parsed.season && e.number === parsed.number);
  }, [slug, parsed?.season, parsed?.number]);

  const all = useMemo(() => {
    const ls = getEpisodesForSeries(slug);
    const fromContent = getSeasonsForSeries(slug).flatMap((season) =>
      season.episodes.map((e) => ({
        id: `${slug}-s${season.season}e${e.episode_number}`,
        seriesSlug: slug,
        season: season.season,
        number: e.episode_number,
        title: localizeEpisodeTitle(e.title, language),
        url: e.watch_url || "",
        thumbnail: e.thumbnail,
        addedAt: 0,
      })),
    );
    const seen = new Set<string>();
    return [...fromContent, ...ls]
      .filter((e) => {
        const k = `s${e.season}e${e.number}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .sort((a, b) => a.season - b.season || a.number - b.number);
  }, [slug]);

  // Episode pagination on the watch page:
  // 10 items per page, resets whenever the season OR series changes.
  const EPS_PER_PAGE = 10;
  const [epPage, setEpPage] = useState(1);
  const others = useMemo(
    () => all.filter((e) => !(parsed && e.season === parsed.season && e.number === parsed.number)),
    [all, parsed?.season, parsed?.number],
  );
  const othersThisSeason = useMemo(
    () => (parsed ? others.filter((e) => e.season === parsed.season) : others),
    [others, parsed?.season],
  );
  const totalEpPages = Math.max(1, Math.ceil(othersThisSeason.length / EPS_PER_PAGE));
  const safeEpPage = Math.min(Math.max(1, epPage), totalEpPages);
  const pagedOthers = othersThisSeason.slice(
    (safeEpPage - 1) * EPS_PER_PAGE,
    safeEpPage * EPS_PER_PAGE,
  );
  // Reset on season / series change.
  useEffect(() => { setEpPage(1); }, [slug, parsed?.season]);

  // Prev/Next episode across ALL seasons (linear order).
  const currentIdx = parsed
    ? all.findIndex((e) => e.season === parsed.season && e.number === parsed.number)
    : -1;
  const prevEp = currentIdx > 0 ? all[currentIdx - 1] : undefined;
  const nextEp = currentIdx >= 0 && currentIdx < all.length - 1 ? all[currentIdx + 1] : undefined;

  // نفضّل watch_token المُشفَّر (لا يكشف الرابط في JSON أو DOM).
  // إذا لم يوجد، نرجع إلى watch_url ونقوم بتشفير داخلي في المتصفح.
  const watchQualities = contentEpisode?.watch_urls ?? [];
  const [selectedQualityIdx, setSelectedQualityIdx] = useState<number>(0);
  useEffect(() => { setSelectedQualityIdx(0); }, [contentEpisode?.episode_number, slug]);
  const selectedQuality = watchQualities[selectedQualityIdx];
  const playToken = selectedQuality ? selectedQuality.token : contentEpisode?.watch_token;
  const playUrl = selectedQuality ? (selectedQuality.url ?? "") : (contentEpisode?.watch_url ?? episode?.url ?? "");
  const downloadUrl = contentEpisode?.download_url;
  void downloadUrl;

  // تحقّق صحّة الجودة المختارة: يجب أن يكون هناك إمّا watch_token غير فارغ
  // أو رابط watch_url يبدأ بـ http/https. نمنع محاولات التشغيل الفاشلة صامتاً.
  const hasValidToken = !!(playToken && playToken.trim());
  const hasValidUrl = !!(playUrl && /^https?:\/\//i.test(playUrl.trim()));
  const qualityIsPlayable = selectedQuality ? hasValidToken && (!playUrl || hasValidUrl) : (hasValidToken || hasValidUrl);
  const qualityValidationError = selectedQuality && !qualityIsPlayable
    ? "هذه الجودة تحتاج watch_token غير فارغ، وإذا أضفت رابطاً يجب أن يبدأ بـ http/https."
    : null;

  if (!series || !parsed || (!episode && !contentEpisode)) {
    return (
      <div className="min-h-dvh flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-extrabold">{t("الحلقة غير موجودة", "Episode not found", "Épisode introuvable")}</h1>
          <Link to="/series/$slug" params={{ slug }} className="btn-primary inline-flex mt-6">
            {t("العودة لصفحة المسلسل", "Back to series", "Retour à la série")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
        <Link
          to="/series/$slug"
          params={{ slug }}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"
        >
          <ArrowLeft className={`h-4 w-4 ${(language === "ar" ? "rotate-180" : "")}`} />
          {pickTitle(series, language)}
        </Link>

        <h1 className="text-xl sm:text-2xl font-extrabold">
          S{parsed.season} · E{parsed.number} — {localizeEpisodeTitle(contentEpisode?.title, language) || episode?.title}
        </h1>

        {watchQualities.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">
                {t("جودة المشاهدة:", "Watch quality:", "Qualité de lecture :")}
              </span>
              {watchQualities.map((q, i) => {
                const url = q.url?.trim() ?? "";
                const token = q.token?.trim() ?? "";
                const valid = !!token && (!url || /^https?:\/\//i.test(url));
                return (
                  <button
                    key={`${q.quality}-${i}`}
                    onClick={() => setSelectedQualityIdx(i)}
                    title={valid ? q.quality : `${q.quality} — بلا رابط/رمز صالح`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-extrabold border transition ${
                      i === selectedQualityIdx
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card text-foreground border-border hover:bg-accent"
                    } ${!valid ? "opacity-50 line-through" : ""}`}
                  >
                    {q.quality}
                  </button>
                );
              })}
            </div>
            {qualityValidationError && (
              <div className="text-[11px] rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-2 py-1.5">
                ⚠️ {qualityValidationError}
              </div>
            )}
          </div>
        )}

        <div className="mt-5">
          {qualityIsPlayable ? (
            <EncryptedPlayer
              key={`${selectedQualityIdx}-${playToken ?? playUrl}`}
              token={playToken}
              source={playUrl}
              poster={contentEpisode?.thumbnail || series.backdrop}
              title={localizeEpisodeTitle(contentEpisode?.title, language) || episode?.title}
            />
          ) : (
            <div className="aspect-video grid place-items-center rounded-2xl border border-border bg-card text-muted-foreground text-center p-6">
              {qualityValidationError ?? t("لا يوجد مصدر فيديو لهذه الحلقة.", "No video source for this episode.", "Aucune source vidéo pour cet épisode.")}
            </div>
          )}
        </div>



        <AdSlot slot="watch_below_player" format="horizontal" />

        <div className="mt-4 flex justify-end">
          <Link
            to="/download/$slug/$ep"
            params={{ slug, ep: `s${parsed.season}e${parsed.number}` }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t("تحميل الحلقة", "Download episode", "Télécharger l’épisode")}
          </Link>
        </div>

        {(prevEp || nextEp) && (
          <nav
            className="mt-6 grid grid-cols-2 gap-2"
            data-testid="watch-prev-next"
            aria-label={t("التنقل بين الحلقات", "Episode navigation", "Navigation des épisodes")}
          >
            {prevEp ? (
              <Link
                to="/watch/$slug/$ep"
                params={{ slug, ep: `s${prevEp.season}e${prevEp.number}` }}
                onClick={() => { import("@/lib/internal-nav").then((m) => m.allowNextInternalNav()); }}
                data-testid="watch-prev"
                className="inline-flex items-center justify-start gap-2 px-3 py-2 rounded-xl bg-card border border-border hover:border-primary transition font-bold text-sm"
              >
                <ChevronLeft className={`h-4 w-4 ${language === "ar" ? "rotate-180" : ""}`} />
                <span className="min-w-0 truncate">
                  {t("السابقة", "Previous", "Précédent")} — S{prevEp.season}·E{prevEp.number}
                </span>
              </Link>
            ) : <span />}
            {nextEp ? (
              <Link
                to="/watch/$slug/$ep"
                params={{ slug, ep: `s${nextEp.season}e${nextEp.number}` }}
                onClick={() => { import("@/lib/internal-nav").then((m) => m.allowNextInternalNav()); }}
                data-testid="watch-next"
                className="inline-flex items-center justify-end gap-2 px-3 py-2 rounded-xl bg-card border border-border hover:border-primary transition font-bold text-sm"
              >
                <span className="min-w-0 truncate">
                  {t("التالية", "Next", "Suivant")} — S{nextEp.season}·E{nextEp.number}
                </span>
                <ChevronRight className={`h-4 w-4 ${language === "ar" ? "rotate-180" : ""}`} />
              </Link>
            ) : <span />}
          </nav>
        )}

        {othersThisSeason.length > 0 && (
          <section className="mt-8" data-testid="watch-more-episodes">
            <h2 className="text-lg font-extrabold mb-3">{t("حلقات أخرى", "More episodes", "Autres épisodes")}</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {pagedOthers.map((e) => (
                <Link
                  key={e.id}
                  to="/watch/$slug/$ep"
                  params={{ slug, ep: `s${e.season}e${e.number}` }}
                  onClick={() => { import("@/lib/internal-nav").then((m) => m.allowNextInternalNav()); }}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-primary transition"
                >
                  <span className="shrink-0 text-xs font-extrabold px-2 py-1 rounded text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
                    S{e.season}·E{e.number}
                  </span>
                  <span className="min-w-0 truncate font-bold">{e.title}</span>
                  <Play className="h-4 w-4 text-primary shrink-0" />
                </Link>
              ))}
            </div>

            {totalEpPages > 1 && (
              <div
                className="mt-4 flex items-center justify-center gap-1.5 flex-wrap"
                data-testid="watch-episode-pagination"
              >
                <button
                  type="button"
                  onClick={() => setEpPage((p) => Math.max(1, p - 1))}
                  disabled={safeEpPage <= 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-extrabold border border-border bg-card hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ep-page-prev"
                >
                  {t("السابق", "Prev", "Préc")}
                </button>
                {Array.from({ length: totalEpPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setEpPage(p)}
                    aria-current={p === safeEpPage ? "page" : undefined}
                    className={`min-w-[2.25rem] px-2.5 py-1.5 rounded-lg text-xs font-extrabold border transition ${
                      p === safeEpPage
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card text-foreground border-border hover:bg-accent"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setEpPage((p) => Math.min(totalEpPages, p + 1))}
                  disabled={safeEpPage >= totalEpPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-extrabold border border-border bg-card hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ep-page-next"
                >
                  {t("التالي", "Next", "Suiv")}
                </button>
              </div>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

/**
 * يمرّر إلى المشغّل *token داخلي* (Inner ciphertext) دائماً — المشغّل هو
 * الوحيد الذي يعرف الطبقة الداخلية. مصادر الإدخال المدعومة:
 *
 *  1) `token` (مفضّل): سلسلة جاهزة من مولّد Safelink.
 *     - إما رابط كامل مثل `https://.../play?t=OUTER` — نستخرج OUTER منه.
 *     - أو OUTER token خام (base64url) كما يحدث في ملفات JSON.
 *     في الحالتين نفكّ الطبقة الخارجية → نحصل على Inner ciphertext ونمرّره.
 *  2) `source` (احتياطي): URL نص صريح — نُشفّره داخلياً لتفادي كشفه في DOM.
 */
function EncryptedPlayer({ token, source, poster, title }: { token?: string; source: string; poster?: string; title?: string }) {
  const [innerToken, setInnerToken] = useState<string | null>(null);
  const [decryptError, setDecryptError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setInnerToken(null);
    setDecryptError(null);

    (async () => {
      const { encryptInner, decryptUrl } = await import("@/lib/safelink-crypto");

      // 1) رابط أو رمز خارجي جاهز
      if (token && token.trim()) {
        let outer = token.trim();
        // إذا كان رابطاً كاملاً، استخرج معامل ?t=
        if (/^https?:\/\//i.test(outer)) {
          try {
            const u = new URL(outer);
            const t = u.searchParams.get("t");
            if (t) outer = t;
          } catch { /* ignore, use as-is */ }
        }
        try {
          const inner = await decryptUrl(outer);
          if (!cancelled) setInnerToken(inner);
          return;
        } catch {
          if (!cancelled) setDecryptError("تعذّر فك تشفير رمز الحلقة — تحقّق من الرمز في ملف JSON.");
          return;
        }
      }

      // 2) رابط نص صريح — شفّر داخلياً
      if (source) {
        try {
          const inner = await encryptInner(source);
          if (!cancelled) setInnerToken(inner);
        } catch {
          if (!cancelled) setDecryptError("تعذّر تجهيز المصدر للتشغيل.");
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token, source]);

  if (decryptError) {
    return (
      <div className="aspect-video grid place-items-center rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive text-center p-6">
        <div>
          <p className="font-extrabold">⚠️ {decryptError}</p>
          <p className="text-xs text-muted-foreground mt-2">
            استخدم لوحة التحكم ← مولّد Safelink لإنشاء رمز جديد ثم انسخه في حقل <code>watch_token</code>.
          </p>
        </div>
      </div>
    );
  }
  if (!innerToken) {
    return <EncryptedPlayerLoading />;
  }
  return <RawanPlayer protectedUrl={innerToken} poster={poster} title={title} />;
}


function EncryptedPlayerLoading() {
  const { language } = useTheme();
  return (
    <div className="aspect-video grid place-items-center rounded-2xl border border-border bg-black text-white/60 text-sm">
      {pickT(language, "جارٍ تجهيز المشغل المشفّر…", "Preparing encrypted player…", "Préparation du lecteur chiffré…")}
    </div>
  );
}


// (تمت إزالة QualitySelector الديكوري لصالح مبدّل الجودة الديناميكي في الأعلى
// الذي يستخدم watchQualities الفعلية ويُبدّل مصدر التشغيل تلقائياً.)

